import React, { useState } from "react";
import { apiUrl } from "../apiConfig.js";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Button,
  Container,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import logo from "../logo.JPG";

function Register() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    company: "",
    email: "",
    phoneNumber: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.username || !formData.password || !formData.email || !formData.company) {
      setError("Username, password, email, and company are required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiUrl("api/v1/auth/register"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Error al registrar usuario");
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fafafa",
      }}
    >
      <Container maxWidth="xs">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <img
            src={logo}
            alt="FinSmart"
            style={{
              height: 64,
              width: 64,
              objectFit: "contain",
              borderRadius: 8,
              marginBottom: 12,
            }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "grey.800", letterSpacing: "-0.02em" }}
          >
            FinSmart
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            p: 4,
          }}
        >
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ mb: 3, fontWeight: 600, color: "grey.700" }}
          >
            Crear Cuenta
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Registro exitoso. Redirigiendo al login...
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Usuario"
                name="username"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.username}
                onChange={handleChange}
                required
              />

              <TextField
                label="Contraseña"
                name="password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                size="small"
                value={formData.password}
                onChange={handleChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Compañía"
                name="company"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.company}
                onChange={handleChange}
                required
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <TextField
                label="Cel"
                name="phoneNumber"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {loading ? "Creando cuenta..." : "Crear Cuenta"}
              </Button>

              <Typography variant="body2" align="center" sx={{ mt: 1, color: "grey.600" }}>
                Ya tienes cuenta?{" "}
                <RouterLink
                  to="/login"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Iniciar Sesión
                </RouterLink>
              </Typography>
            </Box>
          </form>
        </Box>

        <Typography
          variant="caption"
          align="center"
          display="block"
          sx={{ mt: 4, color: "grey.400" }}
        >
          &copy; FinSmart
        </Typography>
      </Container>
    </Box>
  );
}

export default Register;
